import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, UserCog, ShieldCheck, Pencil } from "lucide-react";

const ROLE_LABEL = { admin: "Administrador", presupuestista: "Presupuestista", consulta: "Consulta" };

const SECTION_LABELS = {
  insumos: "Insumos", cuadrillas: "Cuadrillas", equipos: "Equipos", fasar: "FASAR",
  apu: "Análisis APU", presupuesto: "Presupuesto", estimaciones: "Estimaciones", comparar: "Comparar",
  licitaciones: "Licitaciones IA", usuarios: "Usuarios", suscripcion: "Suscripción",
  ajustes: "IA & Ajustes", empresas: "Empresas", seguridad: "Seguridad",
};
const ALL_SECTIONS = Object.keys(SECTION_LABELS);
const ROLE_DEFAULT_SECTIONS = {
  admin: ALL_SECTIONS,
  presupuestista: ["insumos", "cuadrillas", "equipos", "fasar", "apu", "presupuesto", "estimaciones", "comparar", "licitaciones"],
  consulta: ["insumos", "cuadrillas", "equipos", "fasar", "apu", "presupuesto", "estimaciones", "comparar"],
};

const emptyForm = { name: "", email: "", password: "", role: "presupuestista", sections: ROLE_DEFAULT_SECTIONS.presupuestista, can_write: true, company_id: "", superadmin: false };

export default function UsersView() {
  const { user, company } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create; object = edit id
  const [form, setForm] = useState(emptyForm);
  const [companies, setCompanies] = useState([]);
  const [viewCompany, setViewCompany] = useState("");
  const isSuper = !!user?.superadmin;

  const load = async (cid = viewCompany) => {
    try { const { data } = await api.get("/users", { params: cid ? { company_id: cid } : {} }); setUsers(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { if (!user) return; load(); if (isSuper) api.get("/admin/companies").then((r) => setCompanies(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [user]);

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm, company_id: viewCompany || user?.company_id || "" }); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name || "", email: u.email, password: "", role: u.role,
      sections: u.sections || ROLE_DEFAULT_SECTIONS[u.role] || [], can_write: u.can_write ?? (u.role !== "consulta"), superadmin: !!u.superadmin });
    setOpen(true);
  };

  const onRole = (v) => setForm((f) => ({ ...f, role: v, sections: ROLE_DEFAULT_SECTIONS[v] || f.sections, can_write: v === "consulta" ? false : true }));
  const toggleSection = (key) => setForm((f) => ({ ...f, sections: f.sections.includes(key) ? f.sections.filter((s) => s !== key) : [...f.sections, key] }));

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, sections: form.sections, can_write: form.can_write };
        if (form.password) payload.password = form.password;
        if (isSuper) payload.superadmin = form.superadmin;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users/invite", isSuper ? form : { ...form, superadmin: false });
        toast.success(`Usuario ${form.email} creado`);
      }
      setOpen(false); setForm(emptyForm); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Usuario eliminado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="h-12 shrink-0 border-b border-border bg-card flex items-center px-3 gap-2">
        <UserCog className="h-4 w-4 text-primary" />
        <h2 className="font-head font-bold uppercase text-sm tracking-tight">Gestión de Usuarios</h2>
        <span className="font-mono-num text-xs text-muted-foreground">{users.length}</span>
        {isSuper && (
          <Select value={viewCompany || "__own"} onValueChange={(v) => { const cid = v === "__own" ? "" : v; setViewCompany(cid); load(cid); }} data-testid="users-company-select">
            <SelectTrigger className="ml-3 h-8 w-64 rounded-sm text-xs" data-testid="users-company-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all" className="text-xs font-medium">Todas las empresas</SelectItem>
              <SelectItem value="__own">{company?.name || "Mi empresa"} (propia)</SelectItem>
              {companies.filter((c) => c.id !== user?.company_id).map((c) => <SelectItem key={c.id} value={c.id} className="text-xs">{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="flex-1" />
        <Button size="sm" className="h-8 rounded-sm gap-1" onClick={openCreate} data-testid="invite-user-btn"><Plus className="h-4 w-4" /> Nuevo usuario</Button>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="exec-panel overflow-hidden max-w-5xl">
          <table className="qc-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                {isSuper && <th style={{ width: 160 }}>Empresa</th>}
                <th style={{ width: 130 }}>Rol</th>
                <th>Secciones</th>
                <th style={{ width: 90 }} className="text-center">Escritura</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`}>
                  <td>{u.name || "—"}</td>
                  <td className="font-mono-num text-xs">{u.email}</td>
                  {isSuper && <td className="text-xs text-muted-foreground" data-testid={`user-company-${u.email}`}>{u.company_name || "—"}</td>}
                  <td>
                    <span className="inline-flex items-center gap-1 text-xs">
                      {u.superadmin ? <ShieldCheck className="h-3.5 w-3.5 text-amber-500" /> : u.role === "admin" && <ShieldCheck className="h-3.5 w-3.5 text-primary" />}
                      {u.superadmin ? "Super-admin" : (ROLE_LABEL[u.role] || u.role)}
                    </span>
                  </td>
                  <td className="text-xs text-muted-foreground">
                    {u.superadmin ? "Todas (super-admin)" : `${(u.sections || []).length} secciones`}
                  </td>
                  <td className="text-center text-xs">{u.can_write ? "Sí" : "No"}</td>
                  <td className="text-center whitespace-nowrap">
                    <button className="text-muted-foreground hover:text-primary mr-3" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}><Pencil className="h-3.5 w-3.5" /></button>
                    {u.id !== user.id && !u.superadmin && (
                      <button className="text-destructive" onClick={() => remove(u)} data-testid={`delete-user-${u.email}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-head">{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Define el rol base, las secciones con acceso y si el usuario puede editar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-auto pr-1">
            <div><Label className="text-xs">Nombre</Label><Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" /></div>
            <div><Label className="text-xs">Correo</Label><Input type="email" disabled={!!editing} className="mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" /></div>
            <div><Label className="text-xs">{editing ? "Nueva contraseña (opcional)" : "Contraseña temporal"}</Label><Input type="password" className="mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="invite-password" /></div>
            {isSuper && !editing && (
              <div>
                <Label className="text-xs">Empresa</Label>
                <Select value={form.company_id || user?.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="invite-company"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={user?.company_id}>{company?.name || "Mi empresa"} (propia)</SelectItem>
                    {companies.filter((c) => c.id !== user?.company_id).map((c) => <SelectItem key={c.id} value={c.id} className="text-xs">{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {editing && isSuper && <div className="text-[11px] text-muted-foreground">Empresa: <span className="text-foreground font-medium">{editing.company_name || "—"}</span></div>}
            <div>
              <Label className="text-xs">Rol base</Label>
              <Select value={form.role} onValueChange={onRole}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="presupuestista">Presupuestista</SelectItem>
                  <SelectItem value="consulta">Consulta</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Secciones con acceso</Label>
                <span className="text-[11px] text-muted-foreground">{form.sections.length}/{ALL_SECTIONS.length}</span>
              </div>
              <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                {ALL_SECTIONS.map((key) => (
                  <label key={key} className="flex items-center gap-2 text-xs cursor-pointer border border-border rounded-sm px-2 py-1.5 hover:bg-accent/40" data-testid={`section-${key}`}>
                    <input type="checkbox" checked={form.sections.includes(key)} onChange={() => toggleSection(key)} className="accent-primary" />
                    {SECTION_LABELS[key]}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="can-write-toggle">
              <input type="checkbox" checked={form.can_write} onChange={(e) => setForm({ ...form, can_write: e.target.checked })} className="accent-primary h-4 w-4" />
              Permitir edición (crear/editar/borrar). Si se desactiva, el usuario es de solo lectura.
            </label>
            {isSuper && (
              <label className="flex items-center gap-2 text-sm cursor-pointer border border-amber-500/40 bg-amber-500/5 rounded-sm px-2 py-2" data-testid="superadmin-toggle">
                <input type="checkbox" checked={form.superadmin} onChange={(e) => setForm({ ...form, superadmin: e.target.checked })} className="accent-amber-500 h-4 w-4" />
                <span className="flex items-center gap-1"><ShieldCheck className="h-4 w-4 text-amber-500" /> Super-administrador de plataforma (acceso total y gestión de licencias de todas las empresas).</span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm" onClick={save} data-testid="invite-save">{editing ? "Guardar" : "Crear usuario"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
